import { useGateway, useUser } from '@ad-lucy/gateway-provider';
import { Avatar, Icon, Popover, Text } from '@ad-lucy/ui';
import { useFetchMeGqlQuery } from '@codegen';
import { useI18n } from '@i18n/i18n.utils';
import { getAppConfig } from '@modules/config/appConfig';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, type keyframes } from 'styled-components';
import { useLayoutContext } from '../../LayoutContext';
import {
  ANIMATION_DURATION,
  ANIMATION_TIMING_FUNCTION,
  collapseAnimationFooter,
  expandAnimationFooter,
  getRenderSidebarCollapsed,
} from '../animationCore';

type ContainerProps = {
  $animation?: Keyframes;
};

type Keyframes = ReturnType<typeof keyframes>;

const containerAnimation = (props: ContainerProps) =>
  css<ContainerProps>`${props.$animation} forwards ${ANIMATION_DURATION}ms ${ANIMATION_TIMING_FUNCTION};`;

const Container = styled.div<ContainerProps>`
  display: flex;
  padding: ${({ theme }) => theme?.spacing?.xs} 0;
  border-top: 1px solid ${({ theme }) => theme?.colors?.gray?._200};
  animation: ${containerAnimation};
`;

const commonStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: 0;
  background-color: transparent;
`;

const EllipsisText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ToggleButton = styled.button`
  ${commonStyles};
  padding: 0;
  gap: ${({ theme }) => theme?.spacing?.sm};
  border-radius: ${({ theme }) => theme?.shapes?.radius};

  :hover,
  :focus {
    cursor: pointer;
    background-color: ${({ theme }) => theme?.colors?.gray?._100};
  }
`;

const MenuButton = styled.button`
  ${commonStyles};
  height: 40px;
  padding: ${({ theme }) => theme?.spacing?.xs} ${({ theme }) => theme?.spacing?.md};
  gap: ${({ theme }) => theme?.spacing?.xs};

  :hover,
  :focus {
    cursor: pointer;
    background-color: ${({ theme }) => theme?.colors?.gray?._50};
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

type StyledPopoverContentProps = { width?: number };

const StyledPopoverContent = styled(Popover.Content)<StyledPopoverContentProps>`
  overflow: hidden;
  width: ${({ width }) => (width ? `${width}px` : undefined)};
`;

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

// Warning: changing the internal spacing of the elements of this component
// directly affects the sidebar animation
// e.g. changing the left or right padding/margin values
function Footer() {
  const { sidebarCollapsed, sidebarTransitionEnded } = useLayoutContext();
  const gateway = useGateway();
  const user = useUser();
  const fetchMeQuery = useFetchMeGqlQuery();
  const { firstName, lastName, profilePicture } =
    fetchMeQuery.data?.me_v2 ?? {};
  const name = `${firstName ?? ''} ${lastName ?? ''}`;
  const avatarUuid = profilePicture?.uuid;
  const appConfig = getAppConfig();
  const { t } = useI18n();
  const avatar = avatarUuid && `${appConfig.IMG_URL}${avatarUuid}/150`;

  const [width, setWidth] = useState(0);
  const refCallback = useCallback((node: HTMLButtonElement) => {
    if (node) {
      setWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const renderSidebarCollapsed = getRenderSidebarCollapsed(
    sidebarCollapsed,
    sidebarTransitionEnded,
  );
  // biome-ignore lint/suspicious/noExplicitAny: legacy
  let animation: any;
  if (!sidebarTransitionEnded) {
    animation = sidebarCollapsed
      ? collapseAnimationFooter
      : expandAnimationFooter;
  }

  return (
    <Container $animation={animation}>
      <Popover>
        <Popover.Trigger asChild={true} data-testid="adm.sidebar.avatar">
          <ToggleButton ref={refCallback} type="button">
            <StyledAvatar
              data-dd-privacy="mask"
              src={avatar}
              size={40}
              alt=""
            />
            {!renderSidebarCollapsed && (
              <Text
                data-dd-privacy="mask"
                as={EllipsisText}
                size="sm"
                weight="medium"
              >
                {name}
              </Text>
            )}
          </ToggleButton>
        </Popover.Trigger>
        <StyledPopoverContent
          sideOffset={4}
          side="top"
          // side="top-start"
          width={renderSidebarCollapsed ? undefined : width}
          onOpenAutoFocus={(event) => {
            // Radix Popover will focus the first focusable element inside the content container
            // when the Popover toggles open, this will cause the first button element in the container
            // to have the focused styles.
            // To prevent that, we call preventDefault:
            // https://www.radix-ui.com/docs/primitives/components/popover#content
            // But we should have the focus of the page within the Popover content container, therefore we
            // call focus at the target of the event, which is the content container.
            event.preventDefault();
            (event.target as HTMLElement)?.focus?.();
          }}
        >
          <MenuButton
            as={StyledLink}
            title={t('personalSettings.changeSettings')}
            to={'/personal-settings/profile'}
            data-testid={'button.personalSettings'}
          >
            <Text as="span" size="sm">
              <Icon size="sm" type="IconUser" />
            </Text>
            <Text as={EllipsisText} size="sm">
              {t('navigation.personalSettings')}
            </Text>
          </MenuButton>
          {user.require('admin:view') && (
            <MenuButton
              as={StyledLink}
              title={t('organizationSettings.changeSettings')}
              to="/organization-settings"
              data-testid="button.organizationSettings"
            >
              <Text as="span" size="sm">
                <Icon size="sm" type="IconGlobe" />
              </Text>
              <Text as={EllipsisText} size="sm">
                {t('navigation.organizationSettings')}
              </Text>
            </MenuButton>
          )}
          <MenuButton
            onClick={() => gateway.logout()}
            title={t('profile.logout')}
            type="button"
            data-testid="button.minimal.logout"
          >
            <Text as="span" size="sm">
              <Icon size="sm" type="IconLogOut" />
            </Text>
            <Text as={EllipsisText} size="sm">
              {t('profile.logout')}
            </Text>
          </MenuButton>
        </StyledPopoverContent>
      </Popover>
    </Container>
  );
}

export default Footer;
